import {CopyOutlined, DeleteOutlined, FormOutlined, PlusOutlined, SearchOutlined} from '@ant-design/icons'
import {Alert, Button, Card, Input, Modal, Table} from 'antd'
import React, {useEffect, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {ItemResponseSavedReportListItemBeanObject, ListPagination, SavedReportBean} from '../../../api/api'
import * as routes from '../../../constants/routes'
import {useDebounce} from '../../../util/useDebounce'
import {toSort, useSorter} from '../../../util/useSorter'
import {StorageKey, storageReaderFactory, storageWriterFactory} from '../../../util/Storage'
import savedReportApi from './SavedReportApi'
import {reportPeriodLabels} from '../report/columnsHelper';

const Column = Table.Column

const storageWriter = storageWriterFactory('savedReport')
const storageReader = storageReaderFactory('savedReport')

const SavedReportList = () => {
    const navigate = useNavigate()
    const [error, setError] = useState()
    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)
    const [data, setData] = useState<SavedReportBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1,
    })

    const loadData = async (page: number) => {
        const response = await savedReportApi.list(pagination.defaultPageSize, page, toSort(sorter), search)

        const array = response.data as Array<ItemResponseSavedReportListItemBeanObject>
        const beans: SavedReportBean[] = array.map((item) => item.data as SavedReportBean)

        if (beans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage,
            })

            setData(beans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    useEffect(() => {
        const searchParams = storageReader.read(StorageKey.SearchParams)
        const page = searchParams.page || pagination.current
        loadData(page)
    }, [debouncedSearch, sorter])

    const handleDelete = (record: SavedReportBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                try {
                    await savedReportApi.delete(record.id as number)

                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }

                    loadData(page)
                } catch (e) {
                    setError(e.error)
                }
            },
        })
    }

    return (
        <Card
            title="Saved Reports"
            className="list-card"
            extra={
                <Link to={routes.REPORT_BUILDER} className="create-new-link">
                    <Button htmlType="button" type="primary" icon={<PlusOutlined />}>
                        New Report
                    </Button>
                </Link>}
        >
            {error && <Alert message={error} type="error" />}
            <div className="list-filter-group">
                <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name" onChange={(e) => setSearch(e.target.value || undefined)} />
            </div>

            <Table rowKey="id" size="middle" dataSource={data} pagination={{ ...pagination, size: 'default' }} onChange={handleTableChange}>
                <Column sorter key="id" title="ID" dataIndex="id" width={80} />
                <Column
                    sorter
                    key="name"
                    title="Name"
                    dataIndex="name"
                    render={(text, record: SavedReportBean) => <Link to={`/report/${record.id}/edit/load`}>{text}</Link>}
                />
                <Column
                    key="reportPeriod"
                    title="Period"
                    dataIndex="reportPeriod"
                    render={(_: any, record: SavedReportBean) =>
                        record.reportPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE ? (
                            <span>{record.startDate + ' - ' + record.endDate}</span>
                        ) : (
                            <span>{reportPeriodLabels[record.reportPeriod]}</span>
                        )
                    }
                />
                <Column key="modified" title="Last Modified" dataIndex="modified" sorter width={250} />
                <Column
                    align="center"
                    key="action"
                    title="Actions"
                    width={250}

                    render={(_text: string, record: SavedReportBean) => (
                        <span>
              <Button type="text"  onClick={() => navigate(`/report/${record.id}/copy`)} aria-label="Copy saved report" title="Copy">
                <CopyOutlined className="actions-btn" />
              </Button>
              <Button type="text"  onClick={() => navigate(`/report/${record.id}/edit`)} aria-label="Open saved report" title="Report">
                <FormOutlined className="actions-btn" />
              </Button>
              <Button type="text"  danger onClick={() => handleDelete(record)} aria-label="Delete saved report" title="Delete">
                <DeleteOutlined />
              </Button>
            </span>
                    )}
                />
            </Table>
        </Card>
    )
}

export default SavedReportList


// WEBPACK FOOTER //
// ./src/component/app/saved-report/SavedReportList.tsx